/**
 * Brochure Studio — operator-facing editor.
 *
 * Opened from a Hotel, Query or Tour record. Lets the operator:
 *   • Upload photos / videos (drag-drop or file picker)
 *   • Paste YouTube / Vimeo links
 *   • Pick a soft music track from the curated library
 *   • Set title, subtitle, intro paragraph, hero image
 *   • Publish + grab the public pollendots.com URL
 *
 * One brochure per parent record — calling `ensure()` either fetches or
 * creates lazily. UI is mobile-first to match the rest of the workspace.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  CheckCircle2, Copy, Image as ImageIcon, Link2, Loader2, Music, Pause, Play, Share2, Sparkles,
  Star, Trash2, Upload, Video,
} from 'lucide-react';
import BrochureHotelsEditor from './BrochureHotelsEditor';

const API = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_SHARE_BASE = (process.env.REACT_APP_PUBLIC_SHARE_DOMAIN || 'https://pollendots.com').replace(/\/$/, '');

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {(open:boolean)=>void} props.onOpenChange
 * @param {'hotel'|'query'|'tour'} props.ownerKind
 * @param {string} props.ownerId
 * @param {string} [props.defaultTitle]
 * @param {string} [props.brochureId]   When set, edits this exact brochure
 *   instead of the legacy "one per owner" ensure flow.
 * @param {(brochure:object)=>void} [props.onSaved]   Fired whenever the
 *   brochure is created/updated/deleted — caller can refresh state.
 */
export default function BrochureStudio({
  open, onOpenChange, ownerKind, ownerId,
  defaultTitle = '', onSaved, brochureId = '',
}) {
  const [loading, setLoading] = useState(true);
  const [brochure, setBrochure] = useState(null);
  const [music, setMusic] = useState([]);
  const [savingField, setSavingField] = useState('');
  const [uploading, setUploading] = useState(false);
  const [linkInput, setLinkInput] = useState({ kind: 'youtube', url: '' });
  // Single shared <audio> element for previewing music tracks in the picker.
  // Mounting it in a ref keeps state changes from interrupting playback.
  const previewAudioRef = useRef(null);
  const [previewingId, setPreviewingId] = useState('');
  const fileRef = useRef(null);

  // Stop preview audio when the studio closes — never leak music into the
  // workspace background.
  useEffect(() => {
    if (open) return;
    const a = previewAudioRef.current;
    if (a) { a.pause(); a.currentTime = 0; }
    setPreviewingId('');
  }, [open]);

  const togglePreview = (track) => {
    let a = previewAudioRef.current;
    if (!a) {
      a = new Audio();
      a.volume = 0.4;
      a.addEventListener('ended', () => setPreviewingId(''));
      previewAudioRef.current = a;
    }
    if (previewingId === track.id) {
      a.pause(); setPreviewingId(''); return;
    }
    a.src = track.url;
    a.play().then(() => setPreviewingId(track.id)).catch(() => {
      toast.error('Could not load the preview — pick a different track or upload your own.');
    });
  };

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        // Music library is small — fetch once when the studio opens.
        const ml = await fetch(`${API}/api/pollen/brochures/library/music`,
          { credentials: 'include', headers: authHeaders() }).then(r => r.json());
        if (alive) setMusic(ml.items || []);
        if (brochureId) {
          // Direct mode — edit the exact brochure picked in the manager.
          const doc = await fetch(`${API}/api/pollen/brochures/${brochureId}`,
            { credentials: 'include', headers: authHeaders() }).then(r => r.json());
          if (alive) setBrochure(doc);
        } else {
          // Legacy ensure — fetch or lazily create the owner's first brochure.
          let r = await fetch(`${API}/api/pollen/brochures/by-owner/${ownerKind}/${ownerId}`,
            { credentials: 'include', headers: authHeaders() }).then(r => r.json());
          if (!r?.brochure) {
            const c = await fetch(`${API}/api/pollen/brochures`, {
              method: 'POST', credentials: 'include',
              headers: { 'Content-Type': 'application/json', ...authHeaders() },
              body: JSON.stringify({ owner_kind: ownerKind, owner_id: ownerId,
                                     title: defaultTitle || '' }),
            });
            r = { brochure: await c.json() };
          }
          if (alive) setBrochure(r.brochure);
        }
      } catch {
        toast.error('Could not open brochure studio.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, ownerKind, ownerId, defaultTitle, brochureId]);

  // Single source of truth for patching the brochure — keeps the optimistic
  // state in lockstep with the server.
  const patch = async (body, field) => {
    if (!brochure) return;
    setSavingField(field || 'patch');
    try {
      const r = await fetch(`${API}/api/pollen/brochures/${brochure.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      const doc = await r.json();
      setBrochure(doc);
      onSaved && onSaved(doc);
    } catch {
      toast.error('Could not save.');
    } finally {
      setSavingField('');
    }
  };

  const uploadFiles = async (files) => {
    if (!brochure) return;
    if (!files || !files.length) return;
    setUploading(true);
    let added = 0;
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const r = await fetch(`${API}/api/pollen/brochures/${brochure.id}/media`, {
          method: 'POST', credentials: 'include',
          headers: authHeaders(),
          body: fd,
        });
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          toast.error(`${file.name}: ${e.detail || 'upload failed'}`);
          continue;
        }
        added += 1;
      } catch {
        toast.error(`${file.name}: upload failed`);
      }
    }
    setUploading(false);
    // Re-fetch brochure to pick up new gallery items.
    const fresh = await fetch(`${API}/api/pollen/brochures/${brochure.id}`,
      { credentials: 'include', headers: authHeaders() }).then(r => r.json());
    setBrochure(fresh);
    onSaved && onSaved(fresh);
    if (added) toast.success(`${added} file${added === 1 ? '' : 's'} added`);
  };

  const addLink = async () => {
    if (!brochure) return;
    if (!linkInput.url.trim()) { toast.error('Paste a YouTube or Vimeo URL.'); return; }
    setSavingField('link');
    try {
      const r = await fetch(`${API}/api/pollen/brochures/${brochure.id}/links`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(linkInput),
      });
      if (!r.ok) throw new Error();
      const fresh = await fetch(`${API}/api/pollen/brochures/${brochure.id}`,
        { credentials: 'include', headers: authHeaders() }).then(r => r.json());
      setBrochure(fresh);
      onSaved && onSaved(fresh);
      setLinkInput({ kind: 'youtube', url: '' });
      toast.success('Link added');
    } catch {
      toast.error('Could not attach link.');
    } finally {
      setSavingField('');
    }
  };

  const removeMedia = async (mid) => {
    if (!brochure) return;
    if (!window.confirm('Remove this from the brochure?')) return;
    try {
      const r = await fetch(`${API}/api/pollen/brochures/${brochure.id}/media/${mid}`, {
        method: 'DELETE', credentials: 'include', headers: authHeaders(),
      });
      if (!r.ok) throw new Error();
      const fresh = await fetch(`${API}/api/pollen/brochures/${brochure.id}`,
        { credentials: 'include', headers: authHeaders() }).then(r => r.json());
      setBrochure(fresh);
      onSaved && onSaved(fresh);
    } catch { toast.error('Could not remove.'); }
  };

  const setHero = (idx) => patch({ hero_index: idx }, 'hero');
  const publish = () => patch({ is_published: !brochure.is_published }, 'pub');

  const deleteBrochure = async () => {
    if (!brochure) return;
    const ok = window.confirm(
      'Delete this brochure permanently? The public share URL will stop working immediately.',
    );
    if (!ok) return;
    setSavingField('delete');
    try {
      const r = await fetch(`${API}/api/pollen/brochures/${brochure.id}`, {
        method: 'DELETE', credentials: 'include', headers: authHeaders(),
      });
      if (!r.ok) throw new Error();
      toast.success('Brochure deleted');
      onSaved && onSaved({ ...brochure, is_deleted: true });
      onOpenChange(false);
    } catch {
      toast.error('Could not delete brochure.');
    } finally {
      setSavingField('');
    }
  };

  const shareUrl = brochure ? `${PUBLIC_SHARE_BASE}/api/og/brochure/${brochure.slug}` : '';

  const [guestLink, setGuestLink] = useState(null);
  const [guestBusy, setGuestBusy] = useState(false);

  const inviteGuests = async () => {
    setGuestBusy(true);
    try {
      const r = await fetch(`${API}/api/pollen/brochures/${brochure.id}/guest-upload-link`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ enabled: true }),
      });
      if (!r.ok) throw new Error('Could not create the upload link');
      const d = await r.json();
      setGuestLink(d);
      try {
        if (navigator.share) await navigator.share({ text: d.message });
        else { await navigator.clipboard.writeText(d.message); toast.success('Invite message copied — paste it on WhatsApp'); }
      } catch { /* share sheet cancelled */ }
    } catch (e) { toast.error(e.message); }
    setGuestBusy(false);
  };

  const nativeShare = async () => {
    if (!shareUrl) return;
    // The link preview card already shows the cover photo, title and
    // subtitle — the message itself must NOT repeat them. Short, warm
    // invite + the URL on its own line (no `url` field: WhatsApp Android
    // would concatenate it onto the text).
    const isMem = brochure.kind === 'memories';
    const text = (isMem
      ? 'Some journeys stay with us forever 🌸\nCome relive the moments with us:'
      : 'Handpicked with care, just for you 🌸\nTake a look:')
      + '\n\n' + shareUrl;
    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied — share it anywhere');
      }
    } catch (e) {
      // User cancelled share sheet — silent.
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[92vh] overflow-y-auto" data-testid="brochure-studio">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-700" />
            <span className="font-serif" style={{ letterSpacing: '0.1em' }}>
              {brochure?.kind === 'memories' ? 'Memories Studio' : 'Brochure Studio'}
            </span>
          </SheetTitle>
          <SheetDescription className="text-xs text-muted-foreground">
            {brochure?.kind === 'memories'
              ? 'Collect the trip\u2019s photos & videos into one album your guests can relive and share.'
              : `Build a branded Pollen Dots brochure for this ${ownerKind}. Photos, videos, music, one share URL.`}
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-amber-700" />
          </div>
        ) : brochure ? (
          <div className="space-y-5 pt-4 pb-12">
            {/* Title + subtitle + intro */}
            <div className="space-y-3 border-2 border-amber-200 bg-amber-50/50 rounded-lg p-3">
              <div>
                <Label className="text-xs font-semibold text-amber-900 flex items-center gap-1">
                  Brochure title <span className="text-rose-600">*</span>
                </Label>
                <Input className="h-11 text-base font-serif bg-white"
                       placeholder="e.g. Coorg — A Slow Weekend"
                       value={brochure.title || ''}
                       onChange={e => setBrochure({ ...brochure, title: e.target.value })}
                       onBlur={() => patch({ title: brochure.title }, 'title')}
                       data-testid="bro-title" />
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Appears as the cover headline of your brochure.
                </p>
              </div>
              <div>
                <Label className="text-xs">Subtitle</Label>
                <Input className="h-10 bg-white" value={brochure.subtitle || ''}
                       placeholder="One line of mood — e.g. coffee, mist & mornings"
                       onChange={e => setBrochure({ ...brochure, subtitle: e.target.value })}
                       onBlur={() => patch({ subtitle: brochure.subtitle }, 'subtitle')}
                       data-testid="bro-subtitle" />
              </div>
              <div>
                <Label className="text-xs">Opening paragraph (optional)</Label>
                <Textarea rows={3} className="bg-white" value={brochure.intro || ''}
                          onChange={e => setBrochure({ ...brochure, intro: e.target.value })}
                          onBlur={() => patch({ intro: brochure.intro }, 'intro')}
                          placeholder="One slow paragraph that sets the mood. Keep it human."
                          data-testid="bro-intro" />
              </div>
            </div>

            {/* Music picker — hidden until the curated library is populated. */}
            {music.length > 0 && (
            <div className="border rounded-lg p-3 space-y-2">
              <Label className="text-xs flex items-center gap-1">
                <Music className="w-3.5 h-3.5" /> Background music
              </Label>
              <div className="space-y-1">
                {music.map(t => (
                  <div key={t.id}
                       className={`flex items-start gap-2 p-2 rounded border ${brochure.music_track === t.id ? 'bg-amber-50 border-amber-300' : 'hover:bg-muted/40'}`}>
                    <input type="radio" name="music" className="mt-1"
                           value={t.id}
                           checked={brochure.music_track === t.id}
                           onChange={() => patch({ music_track: t.id }, 'music')}
                           data-testid={`bro-music-${t.id}`}
                           aria-label={t.label} />
                    <label htmlFor={`music-${t.id}`} className="flex-1 min-w-0 cursor-pointer"
                           onClick={() => patch({ music_track: t.id }, 'music')}>
                      <span className="block text-sm font-medium">{t.label}</span>
                      <span className="block text-[11px] text-muted-foreground">{t.mood}</span>
                    </label>
                    <button type="button"
                            onClick={(e) => { e.stopPropagation(); togglePreview(t); }}
                            className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 border border-amber-300 bg-white hover:bg-amber-50 text-amber-800"
                            data-testid={`bro-music-preview-${t.id}`}
                            aria-label={previewingId === t.id ? 'Stop preview' : 'Preview track'}>
                      {previewingId === t.id ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* Gallery management */}
            <div className="border rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs flex items-center gap-1">
                  <ImageIcon className="w-3.5 h-3.5" /> Gallery
                  <span className="text-[10px] text-muted-foreground ml-1">
                    ({(brochure.gallery || []).length} item{(brochure.gallery || []).length === 1 ? '' : 's'})
                  </span>
                </Label>
                <p className="text-[10px] text-amber-800 hidden sm:block">
                  <Star className="w-2.5 h-2.5 inline -mt-0.5" /> = cover — leads the page & the WhatsApp/link preview
                </p>
                <input ref={fileRef} type="file" multiple accept="image/*,video/*"
                       className="hidden" data-testid="bro-file-input"
                       onChange={e => uploadFiles(e.target.files)} />
                <Button size="sm" variant="outline" disabled={uploading}
                        className="h-9 text-xs border-emerald-300 bg-emerald-50 text-emerald-900"
                        onClick={() => fileRef.current?.click()}
                        data-testid="bro-upload-btn">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                  Upload
                </Button>
              </div>

              {/* Paste link */}
              <div className="flex items-center gap-2">
                <select className="h-9 text-xs border rounded px-1 bg-white"
                        value={linkInput.kind}
                        onChange={e => setLinkInput({ ...linkInput, kind: e.target.value })}
                        data-testid="bro-link-kind">
                  <option value="youtube">YouTube</option>
                  <option value="vimeo">Vimeo</option>
                </select>
                <Input className="h-9 flex-1 text-xs"
                       placeholder="Paste video URL"
                       value={linkInput.url}
                       onChange={e => setLinkInput({ ...linkInput, url: e.target.value })}
                       data-testid="bro-link-url" />
                <Button size="sm" onClick={addLink} disabled={savingField === 'link'}
                        className="h-9 text-xs bg-sky-700 hover:bg-sky-800"
                        data-testid="bro-link-add">
                  <Link2 className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>

              {/* Tile grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(brochure.gallery || []).map((m, i) => {
                  const isHero = i === (brochure.hero_index || 0);
                  return (
                    <div key={m.id} className={`relative rounded border overflow-hidden ${isHero ? 'ring-2 ring-amber-500' : ''}`}>
                      {m.kind === 'photo' && m.storage_path && (
                        <img src={`${API}/api/pollen/brochures/${brochure.id}/media-preview/${m.id}?auth=${encodeURIComponent(localStorage.getItem('session_token') || '')}`}
                             alt=""
                             className="w-full h-auto block" />
                      )}
                      {m.kind === 'video' && (
                        <div className="w-full aspect-video bg-slate-900 text-white flex items-center justify-center">
                          <Video className="w-6 h-6" />
                        </div>
                      )}
                      {(m.kind === 'youtube' || m.kind === 'vimeo') && (
                        <div className="w-full aspect-video bg-rose-900 text-white flex flex-col items-center justify-center text-[10px]">
                          <Link2 className="w-5 h-5 mb-1" />
                          {m.kind === 'youtube' ? 'YouTube' : 'Vimeo'}
                        </div>
                      )}
                      <div className="absolute top-1 left-1 right-1 flex items-center justify-between">
                        <button type="button"
                                onClick={() => setHero(i)}
                                title={isHero ? 'This is the cover' : 'Set as cover'}
                                className={`h-6 rounded-full flex items-center gap-1 justify-center px-1.5 text-[9px] font-medium ${isHero ? 'bg-amber-500 text-white' : 'bg-white/80 hover:bg-white text-amber-700'}`}
                                data-testid={`bro-hero-${i}`}>
                          <Star className="w-3 h-3" />{isHero ? 'COVER' : ''}
                        </button>
                        <button type="button"
                                onClick={() => removeMedia(m.id)}
                                className="h-6 w-6 rounded-full bg-white/80 hover:bg-rose-50 text-rose-700 flex items-center justify-center"
                                data-testid={`bro-remove-${i}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {!(brochure.gallery || []).length && (
                  <p className="col-span-full text-[11px] text-muted-foreground italic">
                    Add at least one photo or video to publish.
                  </p>
                )}
              </div>
            </div>

            {/* Hotel sections — operator can group room categories per
                hotel and attach their own photos / videos / links. The
                flat gallery above remains for general mood / scenery. */}
            <BrochureHotelsEditor
              brochure={brochure}
              onSaved={(fresh) => { setBrochure(fresh); onSaved && onSaved(fresh); }}
            />

            {/* Publish + share */}
            <div className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Publish</Label>
                <Badge className={brochure.is_published ? 'bg-emerald-600 text-white' : 'bg-slate-400 text-white'}>
                  {brochure.is_published ? 'Live' : 'Draft'}
                </Badge>
              </div>
              <Button size="sm"
                      disabled={savingField === 'pub' || !(brochure.gallery || []).length}
                      onClick={publish}
                      className={`w-full h-11 text-white ${brochure.is_published ? 'bg-slate-700 hover:bg-slate-800' : 'bg-emerald-700 hover:bg-emerald-800'}`}
                      data-testid="bro-publish">
                {savingField === 'pub' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> :
                  brochure.is_published ? <CheckCircle2 className="w-4 h-4 mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                {brochure.is_published ? 'Unpublish' : 'Publish brochure'}
              </Button>
              {brochure.is_published && (
                <>
                  <Label className="text-xs mt-2 block">Share URL</Label>
                  <div className="flex items-center gap-2">
                    <Input readOnly className="h-9 text-xs flex-1 bg-muted/40"
                           value={shareUrl} data-testid="bro-share-url" />
                    <Button size="sm" variant="outline" className="h-9"
                            onClick={async () => {
                              try { await navigator.clipboard.writeText(shareUrl); toast.success('Link copied'); }
                              catch { toast.error('Could not copy'); }
                            }}
                            data-testid="bro-share-copy">
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <Button size="sm"
                          onClick={nativeShare}
                          className="w-full h-10 mt-2 bg-amber-700 hover:bg-amber-800 text-white"
                          data-testid="bro-share-native">
                    <Share2 className="w-4 h-4 mr-1.5" />
                    Share brochure
                  </Button>
                  <a href={shareUrl} target="_blank" rel="noreferrer"
                     className="block text-center text-xs underline mt-1 text-sky-700">
                    Open brochure ↗
                  </a>
                </>
              )}
            </div>

            {/* Guest uploads — let customers add their own photos/videos */}
            <div className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-3 space-y-2">
              <Label className="text-xs text-emerald-900 font-semibold">Guest uploads</Label>
              <p className="text-[11px] text-emerald-800">
                Share a branded pollendots.com link — guests add their photos &amp; videos
                straight into this album, no login needed.
              </p>
              <Button size="sm"
                      disabled={guestBusy}
                      onClick={inviteGuests}
                      className="w-full h-10 bg-emerald-700 hover:bg-emerald-800 text-white"
                      data-testid="bro-guest-invite">
                {guestBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Share2 className="w-4 h-4 mr-1.5" />}
                Invite guests to add photos
              </Button>
              {(guestLink || brochure.guest_upload_enabled) && (
                <Button size="sm" variant="outline"
                        disabled={guestBusy}
                        onClick={async () => {
                          setGuestBusy(true);
                          try {
                            const r = await fetch(`${API}/api/pollen/brochures/${brochure.id}/guest-upload-link`, {
                              method: 'POST', credentials: 'include',
                              headers: { 'Content-Type': 'application/json', ...authHeaders() },
                              body: JSON.stringify({ enabled: false }),
                            });
                            if (!r.ok) throw new Error('Failed');
                            setGuestLink(null);
                            setBrochure({ ...brochure, guest_upload_enabled: false });
                            toast.success('Guest uploads stopped — the link no longer works');
                          } catch { toast.error('Could not stop guest uploads'); }
                          setGuestBusy(false);
                        }}
                        className="w-full h-9 border-emerald-300 text-emerald-800"
                        data-testid="bro-guest-stop">
                  Stop guest uploads
                </Button>
              )}
              {guestLink && (
                <div className="flex items-center gap-2">
                  <Input readOnly className="h-9 text-xs flex-1 bg-white"
                         value={guestLink.url} data-testid="bro-guest-url" />
                  <Button size="sm" variant="outline" className="h-9"
                          onClick={async () => {
                            try { await navigator.clipboard.writeText(guestLink.message); toast.success('Invite copied'); }
                            catch { toast.error('Could not copy'); }
                          }}
                          data-testid="bro-guest-copy">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>

            {/* Danger zone — delete the entire brochure. */}
            <div className="border border-rose-200 bg-rose-50/40 rounded-lg p-3 space-y-2">
              <Label className="text-xs text-rose-900 font-semibold">Danger zone</Label>
              <p className="text-[11px] text-rose-700">
                Permanently delete this brochure. The public share URL stops working
                immediately and gallery uploads cannot be recovered.
              </p>
              <Button size="sm" variant="outline"
                      disabled={savingField === 'delete'}
                      onClick={deleteBrochure}
                      className="w-full h-10 border-rose-300 bg-white text-rose-700 hover:bg-rose-100"
                      data-testid="bro-delete">
                {savingField === 'delete'
                  ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  : <Trash2 className="w-4 h-4 mr-1" />}
                Delete brochure
              </Button>
            </div>

            <p className="text-[10px] text-muted-foreground text-center">
              Brochures unfurl beautifully at
              <strong> pollendots.com/api/og/brochure/&lt;slug&gt;</strong> — link
              previews show a hero photo + title on WhatsApp, iMessage and email.
              Audio autoplay stays OFF — viewers tap the pill to play.
            </p>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
